import { Mail, Phone, Clock } from "lucide-react";
import businessCallImg from "@/assets/business-call.webp";

export default function LandingContact() {
  return (
    <>
      <style>{`
        .landing-contact-section {
          width: 100%;
          position: relative;
          padding: 80px 20px;
          overflow: hidden;
        }

        .landing-contact-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url(${businessCallImg});
          background-size: cover;
          background-position: center;
          filter: brightness(1.2);
          z-index: 0;
        }

        .landing-contact-section::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(15, 23, 42, 0.7), rgba(30, 41, 59, 0.8));
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          z-index: 1;
        }

        .landing-contact-container {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 60px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .landing-contact-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 60px;
        }

        @media (min-width: 768px) {
          .landing-contact-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        .landing-contact-left {
          color: white;
        }

        .landing-contact-heading {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .landing-contact-subtext {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .landing-contact-info {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .landing-contact-pill {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 50px;
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.3s ease;
        }

        .landing-contact-pill:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-2px);
        }

        .landing-contact-pill-icon {
          width: 20px;
          height: 20px;
          color: #f59e0b;
        }

        .landing-contact-pill-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .landing-contact-pill-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: rgba(255, 255, 255, 0.5);
        }

        .landing-contact-pill-value {
          font-size: 1rem;
          color: white;
          font-weight: 500;
        }

        .landing-contact-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .landing-contact-label {
          display: flex;
          flex-direction: column;
          gap: 8px;
          color: white;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .landing-contact-input,
        .landing-contact-textarea {
          width: 100%;
          padding: 14px 18px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
        }

        .landing-contact-input::placeholder,
        .landing-contact-textarea::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .landing-contact-input:focus,
        .landing-contact-textarea:focus {
          background: rgba(255, 255, 255, 0.12);
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .landing-contact-textarea {
          resize: vertical;
          min-height: 120px;
          font-family: inherit;
        }

        .landing-contact-button {
          width: 100%;
          padding: 16px 32px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(245, 158, 11, 0.3);
        }

        .landing-contact-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(245, 158, 11, 0.4);
        }

        .landing-contact-button:active {
          transform: translateY(0);
        }

        @media (max-width: 767px) {
          .landing-contact-container {
            padding: 40px 24px;
          }

          .landing-contact-heading {
            font-size: 2rem;
          }

          .landing-contact-grid {
            gap: 40px;
          }
        }
      `}</style>

      <section className="landing-contact-section">
        <div className="landing-contact-container">
          <div className="landing-contact-grid">
            {/* Left Column */}
            <div className="landing-contact-left">
              <h2 className="landing-contact-heading">Send us a message</h2>
              <p className="landing-contact-subtext">
                We'd love to hear from you. Our team is ready to assist with your luxury transportation needs.
              </p>
              
              <div className="landing-contact-info">
                <div className="landing-contact-pill">
                  <Mail className="landing-contact-pill-icon" />
                  <div className="landing-contact-pill-content">
                    <span className="landing-contact-pill-label">Email</span>
                    <span className="landing-contact-pill-value"></span>
                  </div>
                </div>

                <div className="landing-contact-pill">
                  <Phone className="landing-contact-pill-icon" />
                  <div className="landing-contact-pill-content">
                    <span className="landing-contact-pill-label">Phone</span>
                    <span className="landing-contact-pill-value"></span>
                  </div>
                </div>

                <div className="landing-contact-pill">
                  <Clock className="landing-contact-pill-icon" />
                  <div className="landing-contact-pill-content">
                    <span className="landing-contact-pill-label">Hours</span>
                    <span className="landing-contact-pill-value">24/7 Available</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div>
              <form
                className="landing-contact-form"
                action="https://formspree.io/f/mrbydzwy"
                method="POST"
              >
                <label className="landing-contact-label">
                  Name
                  <input
                    type="text"
                    name="name"
                    placeholder="Jane Doe"
                    required
                    className="landing-contact-input"
                  />
                </label>

                <label className="landing-contact-label">
                  Email
                  <input
                    type="email"
                    name="email"
                    placeholder="jane@email.com"
                    required
                    className="landing-contact-input"
                  />
                </label>

                <label className="landing-contact-label">
                  Phone
                  <input
                    type="tel"
                    name="phone"
                    placeholder="210-296-7409"
                    required
                    className="landing-contact-input"
                  />
                </label>

                <label className="landing-contact-label">
                  Message
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="How can we help?"
                    required
                    className="landing-contact-textarea"
                  />
                </label>

                <button type="submit" className="landing-contact-button">
                  Send message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
